import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { missions } from '../data/missions';
import type { MissionId } from '../data/missions';
import { useLanguage } from '../i18n/LanguageContext';
import { copy } from '../i18n/copy';

interface TaaBotProps {
  onSelectMission: (id: MissionId) => void;
}

export default function TaaBot({ onSelectMission }: TaaBotProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  // Greet the visitor shortly after the page loads.
  useEffect(() => {
    const timer = window.setTimeout(() => setOpen(true), 1600);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="fixed bottom-5 end-5 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="w-80 rounded-2xl border border-white/10 bg-navy-900/95 p-4 shadow-2xl backdrop-blur-xl"
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-white">
                    Taa Bot
                    <span className="relative inline-flex size-2.5">
                      <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                      <span className="relative inline-flex size-2.5 rounded-full bg-emerald-400" />
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close assistant"
                    className="rounded-md p-1 text-slate-400 transition-colors hover:text-white"
                  >
                    <X className="size-4" />
                  </button>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{t(copy.botMessage)}</p>
              </div>
              <img
                src="/assets/robot.png"
                alt="Taa Bot"
                className="size-20 shrink-0 select-none object-contain [mask-image:radial-gradient(circle_at_center,black_55%,transparent_75%)]"
              />
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2">
              {missions.map((mission) => (
                <button
                  key={mission.id}
                  type="button"
                  onClick={() => onSelectMission(mission.id)}
                  className={`rounded-lg px-2 py-2 text-xs font-bold transition-all ${mission.button}`}
                >
                  {t(mission.navLabel)}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open Taa Bot assistant"
        aria-expanded={open}
        className="relative size-14 overflow-hidden rounded-full bg-navy-900 ring-2 ring-cyan-400/60 shadow-[0_0_40px_-8px_rgba(34,211,238,0.8)]"
      >
        <img src="/assets/robot.png" alt="" className="absolute inset-0 size-full object-cover" />
        <span className="absolute -end-0.5 -top-0.5 size-3 rounded-full bg-amber-400">
          <span className="absolute inset-0 animate-ping rounded-full bg-amber-400" />
        </span>
      </motion.button>
    </div>
  );
}
